import React, { useState } from 'react'
import { useWindowWidth } from '@react-hook/window-size'
import {
  Anchor,
  BrandTypes,
  Button,
  Div,
  DropDownMenu,
  Flex,
  HStack,
  Icon,
  LogoColorTypes,
  QuantumThemeWrapper,
  Spacer,
  StaticNavigation,
  Text,
  UserCard,
  UserCardLoader,
} from '@sendoutcards/quantum-design-ui'
import { StorefrontState } from 'src/redux/reducers/storefront'
import { SponsorFragment } from 'src/graphql'

const links = [
  {
    title: 'Learn More',
    linkColumns: [
      {
        title: 'Overview',
        linkTitleColor: 'secondaryBrand',
        subLinkColor: 'primaryBody',
        links: [
          {
            title: 'SendOutCards | The Overview',
            href: 'https://www.youtube.com/watch?v=16I2KxaZ9Zo',
          },
        ],
      },
    ],
  },
  {
    title: 'Shop',
    linkColumns: [
      {
        title: 'Featured',
        links: [
          {
            title: 'Card Catalog',
            href: 'https://www.sendoutcards.com/app/catalog',
          },
        ],
      },
      {
        title: 'Top Card Categories',
        links: [
          {
            title: 'Spring Collection',
            href: 'https://www.sendoutcards.com/app/catalog/collections/158',
          },
          {
            title: 'Thank You Cards',
            href: 'https://www.sendoutcards.com/app/catalog/categories/790',
          },
          {
            title: 'Birthday Cards',
            href: 'https://www.sendoutcards.com/app/catalog/categories/787',
          },
        ],
      },
    ],
  },
]

const logo = {
  brand: 'soc' as BrandTypes,
  color: 'color' as LogoColorTypes,
}

const accountSectionLinks = {
  login:
    'https://app.sendoutcards.com/dashboard/account?client=soc&redirect_uri=https%3A%2F%2Fapp.sendoutcards.com%2Fcatalog',
  join:
    'https://app.sendoutcards.com/dashboard/account?client=soc&join&redirect_uri=https%3A%2F%2Fapp.sendoutcards.com%2Fcatalog',
}

type AccountSectionProps = {
  sponsor?: SponsorFragment
}

const AccountSection = (props: AccountSectionProps) => {
  const { sponsor } = props
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const width = useWindowWidth()
  const isMobile = width < 600

  const mobileDropdownLinks = {
    title: 'My Account',
    links: [
      { title: 'Login', href: accountSectionLinks.login },
      { title: 'Join Now', href: accountSectionLinks.join },
    ],
  }

  const sponsorSuffix = (): string => {
    if (!sponsor) return ''

    return sponsor.genealogyId
      ? `&sponsor=${sponsor.genealogyId}`
      : sponsor.storefront && sponsor.storefront.slug
      ? `&sponsor=${sponsor.storefront.slug}`
      : ''
  }

  const joinLink = accountSectionLinks.join.concat(sponsorSuffix())

  return isMobile ? (
    <Flex position="relative">
      <Icon name="user" size="xSmall" primaryColor="primaryBodyText" />
      <Icon
        name={'chevron'}
        orientation={isDropdownOpen ? 'up' : 'down'}
        size={'small'}
        onClick={() => {
          setIsDropdownOpen(!isDropdownOpen)
        }}
        primaryColor={'primaryBodyText'}
      />
      <DropDownMenu
        right={-24}
        top={48}
        isOpen={isDropdownOpen}
        linkColumn={mobileDropdownLinks}
      />
    </Flex>
  ) : (
    <Div width="200px">
      <HStack justify="center" gap="x2">
        <Anchor
          href={accountSectionLinks.login}
          style={{ textDecoration: 'none' }}
        >
          <Button size="xSmall" type="shadow" hover={true}>
            Login
          </Button>
        </Anchor>
        <Anchor href={joinLink} style={{ textDecoration: 'none' }}>
          <Button hover={true} outlined={true} size="xSmall">
            Join Now
          </Button>
        </Anchor>
      </HStack>
    </Div>
  )
}

type StaticNavProps = {
  storefront: StorefrontState
}

const StaticNav: React.FC<StaticNavProps> = props => {
  const { storefront } = props

  const isLoading = storefront.loading && !storefront.storefront?.user
  const hasSponsor = storefront.storefront?.user.genealogyId

  const [isBioDisplayed, toggleIsBioDisplayed] = useState<boolean>(false)

  const width = useWindowWidth()
  const isMobile = width < 600

  const drawerOffset = hasSponsor ? 120 : 60

  return (
    <QuantumThemeWrapper>
      <Div position="fixed" width="100%" zIndex={102} minWidth="320px">
        {hasSponsor && (
          <Flex
            inset={{ left: 'x3', top: 'x1' }}
            minHeight="60px"
            position="relative"
            backgroundColor="background"
            flexDirection="column"
            zIndex={22}
          >
            <Flex
              justifyContent="flex-start"
              alignItems="center"
              alignContent="center"
            >
              {isLoading ? (
                <UserCardLoader size="small" />
              ) : (
                <>
                  <UserCard
                    profileImage={
                      storefront.storefront?.user.storefront?.mugshot?.url ??
                      storefront.storefront?.user.profileImageUrl ??
                      ''
                    }
                    firstName={storefront.storefront?.user.firstName ?? ''}
                    lastName={storefront.storefront?.user.lastName ?? ''}
                    caption={`Thanks for visiting my ${
                      storefront.storefront?.user.isAffiliate
                        ? 'affiliate'
                        : 'referral'
                    } site.`}
                    size="small"
                  />
                  <Spacer orientation="horizontal" space="x2" />
                  <Button
                    title={isBioDisplayed ? 'Show Less' : 'About Me'}
                    size="xSmall"
                    type="secondary"
                    onClick={() => toggleIsBioDisplayed(!isBioDisplayed)}
                  />
                  <Spacer orientation="horizontal" space="x2" />
                  {isBioDisplayed && !isMobile && (
                    <Text type="body">
                      {storefront.storefront?.user.storefront?.biography}
                    </Text>
                  )}
                </>
              )}
            </Flex>
            {isMobile && isBioDisplayed && (
              <Flex justifyContent="center" alignItems="center">
                <Text type="body">
                  {storefront.storefront?.user.storefront?.biography}
                </Text>
              </Flex>
            )}
          </Flex>
        )}
        <StaticNavigation
          links={links}
          logo={logo}
          drawerOffset={drawerOffset}
          accountSection={
            <AccountSection sponsor={storefront.storefront?.user} />
          }
        />
      </Div>
    </QuantumThemeWrapper>
  )
}

export default StaticNav
